import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { addCartItem, logOut, removeCartItem, updateCartItemPortion, updateCartItemQuantity } from '../redux/actions/action-creator'
import CartSegmentWarningModal from './CartSegmentWarningModal'
import GuestLoginRequiredModal from './GuestLoginRequiredModal'
import ProductImage from './common/ProductImage'
import ProductPortionModal from './ProductPortionModal'
import MarketPromoBanner from './MarketPromoBanner'
import { getActiveCartStoreName, hasCartSegmentMismatch } from '../utils/cartSegmentGuard'
import { getMarketMetadata } from '../utils/locationDefaults'
import { getProductDetailLink } from '../utils/productDetailNavigation'
import {
    getProductVariantId, getProductUnit,
    getProductPrice, getOriginalPrice, getProductPortions, hasPrice,
} from '../utils/productOptions'
import './shopsection.css'

const DUMMY_SUBCATEGORY_IMAGES = [
    `${process.env.PUBLIC_URL}/assets/images/category/category1.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category2.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category3.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category4.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category5.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category6.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/category7.png`,
    `${process.env.PUBLIC_URL}/assets/images/category/Green-Broccoli-Transparent-Images-PNG 7.png`,
];

const ShopSection = ({
    categoryProduct,
    loading = false,
    error = "",
    subCategories = [],
    subCategoriesLoading = false,
    selectedSubCategoryId = "",
    onSubCategorySelect,
    currentPage = 1,
    onPageChange,
    cartContext = {},
    productDetailContext = {},
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { userData = {}, user_location, country_id } = useSelector((state) => state.auth)
    const cartState = useSelector((state) => state.cart || {})
    const storesData = useSelector((state) => state.common?.storesData)
    const cartItems = cartState?.cartItems || []
    const [showGuestModal, setShowGuestModal] = useState(false)
    const [pendingCartItem, setPendingCartItem] = useState(null)
    const [selectedPortions, setSelectedPortions] = useState({})
    const [portionPicker, setPortionPicker] = useState(null)
    const isGuest = Boolean(userData?.guest)
    const activeMarket = getMarketMetadata({ ...(user_location || {}), country_id })
    const productCandidates = [
        categoryProduct?.data?.data,
        categoryProduct?.data?.products,
        categoryProduct?.data?.items,
        categoryProduct?.data?.response_data,
        categoryProduct?.products,
        categoryProduct?.items,
        categoryProduct?.data,
    ];
    const products = productCandidates.find(Array.isArray) || []

    let [grid, setGrid] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")

    let [active, setActive] = useState(false)
    let sidebarController = () => {
        setActive(!active)
    }

    const getProductId = (item, index) =>
        String(getProductVariantId(item) || item.product_id || `${item.title || item.name}-${index}`);

    const getSubCategoryId = (item, index) =>
        String(
            item?.sub_category_id ??
            item?.subcategory_id ??
            item?.subCategoryId ??
            item?.category_id ??
            item?.categoryId ??
            item?.product_category_id ??
            item?.id ??
            index
        );

    const getSubCategoryTitle = (item, index) =>
        item?.title || item?.name || item?.category_name || item?.subcategory_name || `Subcategory ${index + 1}`;

    const getSubCategoryCount = (item) =>
        item?.total_products || item?.product_count || item?.products_count || item?.count;

    const getSubCategoryImage = (item, index = 0) =>
        item?.image ||
        item?.icon ||
        item?.category_image ||
        item?.subcategory_image ||
        item?.sub_category_image ||
        item?.product_category_image ||
        DUMMY_SUBCATEGORY_IMAGES[index % DUMMY_SUBCATEGORY_IMAGES.length];

    const getProductTitle = (item, index) =>
        item?.title || item?.name || item?.product_name || item?.product_title ||
        item?.product?.title || item?.product?.name || item?.product?.product_name || `Product ${index + 1}`;

    const getProductImage = (item) =>
        item?.image || item?.product_cover_image || item?.product_image || "";

    const getProductRating = (item) =>
        item?.rating || item?.avg_rating || item?.product_rating || "4.5";

    const getProductCurrency = (item) =>
        item?.currency || item?.currency_code || item?.currencyCode || activeMarket.currency;

    const isProductOutOfStock = (item) => {
        const status = String(item?.stock_status || item?.availability || item?.status || "").toLowerCase();
        const explicitStatus = item?.is_out_of_stock ?? item?.out_of_stock ?? item?.isOutOfStock;
        const stockValue = item?.stock_quantity ?? item?.available_quantity ?? item?.inventory ?? item?.stock;

        if (status.includes("out")) return true;
        if (explicitStatus === true || explicitStatus === 1 || explicitStatus === "1" || explicitStatus === "true") return true;
        if (stockValue !== null && stockValue !== undefined && stockValue !== "" && Number(stockValue) <= 0) return true;

        return false;
    };

    const isAllSubCategory = (item, index) => {
        const id = getSubCategoryId(item, index);
        const title = getSubCategoryTitle(item, index).trim().toLowerCase();

        return id === "0" || title === "all";
    };

    const visibleSubCategories = subCategories.filter(
        (item, index) => !isAllSubCategory(item, index)
    );

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const displayedProducts = normalizedSearchTerm
        ? products.filter((item, index) =>
            getProductTitle(item, index).toLowerCase().includes(normalizedSearchTerm)
        )
        : products;

    const getCartItem = (item, index) => {
        const productId = getProductId(item, index);
        return cartItems.find((cartItem) => String(cartItem.id) === productId);
    };

    const getSelectedPortion = (item, index) => {
        const portions = getProductPortions(item);
        const selectedUnit = selectedPortions[getProductId(item, index)];
        return portions.find((portion) => portion.portion === selectedUnit) || portions[0];
    };

    const submitPortion = async (portion) => {
        const { item, index } = portionPicker;
        const cartItem = getCartItem(item, index);
        if (cartItem && cartItem.portion !== portion.portion) {
            const response = await dispatch(updateCartItemPortion(cartItem.id, {
                portion: portion.portion,
                price: Number(getProductPrice(item, portion)),
            }));
            if (!response) {
                setPortionPicker(null);
                return;
            }
        }
        setSelectedPortions((current) => ({ ...current, [getProductId(item, index)]: portion.portion }));
        setPortionPicker(null);
    };

    const buildCartItem = (item, index) => {
        const productVariantId = getProductVariantId(item);
        const selectedPortion = getSelectedPortion(item, index);
        if (!productVariantId) return null;

        return {
            id: String(productVariantId),
            product_variant_id: productVariantId,
            name: getProductTitle(item, index),
            price: Number(getProductPrice(item, selectedPortion)),
            quantity: 1,
            image: item.image || item.product_cover_image || 'assets/images/thumbs/product-two-img1.png',
            currency: getProductCurrency(item),
            rating: item.rating,
            portion: selectedPortion?.portion || null,
            unit: selectedPortion ? "" : getProductUnit(item),
            merchant_id: item.merchant_id || item.merchantId || cartContext.merchant_id,
            empty_bottle_count: item.empty_bottle_count ?? item.emptyBottleCount ?? null,
            arr_option_id: item.arr_option_id ?? item.arrOptionId ?? null,
            segment_id: item.segment_id || item.segmentId || item.segment || cartContext.segment_id || cartContext.segmentId,
            business_segment_id:
                item.business_segment_id ||
                item.businessSegmentId ||
                item.store_id ||
                item.storeId ||
                cartContext.business_segment_id ||
                cartContext.businessSegmentId
        };
    };

    const increaseQuantity = (item, index) => {
        if (cartState.saveCartLoading || cartState.getCartLoading) return;
        if (isGuest) {
            setShowGuestModal(true);
            return;
        }

        const cartItem = buildCartItem(item, index);
        if (!cartItem) return;

        if (hasCartSegmentMismatch(cartState, cartItem)) {
            setPendingCartItem(cartItem);
            return;
        }

        dispatch(addCartItem(cartItem));
    };

    const closeCartSegmentWarning = () => {
        setPendingCartItem(null);
    };

    const confirmCartSegmentWarning = () => {
        if (!pendingCartItem || cartState.saveCartLoading || cartState.getCartLoading) {
            return;
        }

        dispatch(addCartItem(pendingCartItem, { replaceCart: true }));
        setPendingCartItem(null);
    };

    const handleGuestContinue = async () => {
        setShowGuestModal(false);
        await dispatch(logOut());
        navigate('/login');
    };

    const decreaseQuantity = (item, index) => {
        const cartItem = getCartItem(item, index);

        if (!cartItem) {
            return;
        }

        if (Number(cartItem.quantity || 0) <= 1) {
            dispatch(removeCartItem(cartItem.id));
            return;
        }

        dispatch(updateCartItemQuantity(cartItem.id, Number(cartItem.quantity) - 1));
    };

    const renderProductLoader = () => (
        <div className="shop-loader-grid">
            {Array.from({ length: 8 }).map((_, index) => (
                <div className="shop-loader-card" key={index}>
                    <div className="shop-loader-thumb" />
                    <div className="shop-loader-line w-75" />
                    <div className="shop-loader-line w-50" />
                    <div className="shop-loader-line w-100" />
                    <div className="shop-loader-button" />
                </div>
            ))}
        </div>
    );

    const getPaginationInfo = () => {
        const sources = [
            categoryProduct?.data?.pagination,
            categoryProduct?.data?.meta,
            categoryProduct?.pagination,
            categoryProduct?.meta,
            categoryProduct?.data,
            categoryProduct,
        ].filter((item) => item && typeof item === "object" && !Array.isArray(item));
        const source =
            sources.find((item) =>
                item.current_page ||
                item.currentPage ||
                item.last_page ||
                item.lastPage ||
                item.total_pages ||
                item.totalPages ||
                item.total
            ) || {};
        const page = Number(source.current_page || source.currentPage || source.page || currentPage) || 1;
        const perPage = Number(source.per_page || source.perPage || source.limit || products.length) || products.length || 0;
        const total = Number(source.total || source.total_count || source.totalCount || 0);
        const fallbackPages = total && perPage ? Math.ceil(total / perPage) : 1;
        const totalPages =
            Number(
                source.last_page ||
                source.lastPage ||
                source.total_pages ||
                source.totalPages ||
                source.pages ||
                fallbackPages
            ) || 1;

        return {
            current: page,
            total,
            totalPages,
        };
    };

    const getPaginationPages = (current, totalPages) => {
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const halfWindow = Math.floor(maxVisible / 2);
        let start = Math.max(1, current - halfWindow);
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    };

    const paginationInfo = getPaginationInfo();
    const paginationPages = getPaginationPages(
        paginationInfo.current,
        paginationInfo.totalPages
    );

    return (
      <>
        <MarketPromoBanner page="shop" segmentId={cartContext.segment_id} businessSegmentId={cartContext.business_segment_id}
          categoryId={productDetailContext.parentCategoryId || productDetailContext.categoryId} />
        <section className="shop">
            <div className={`side-overlay ${active && "show"}`}></div>
            <div className="container container-lg">
                <div className="row">
                    {/* Sidebar Start */}
                    <div className="col-lg-3">
                        <div className={`shop-sidebar ${active && "active"}`}>
                            <button onClick={sidebarController}
                                type="button"
                                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
                            >
                                <i className="ph ph-x" />
                            </button>
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Sub Category
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    {subCategoriesLoading && (
                                        <li className="shop-subcategory-empty">Loading subcategories...</li>
                                    )}
                                    {!subCategoriesLoading && (
                                        <li className="mb-12">
                                            <button
                                                type="button"
                                                className={`shop-subcategory-btn ${String(selectedSubCategoryId) === "0" ? "active" : ""}`}
                                                onClick={() => onSubCategorySelect?.({ id: 0, title: "All" })}
                                                aria-pressed={String(selectedSubCategoryId) === "0"}
                                            >
                                                <span>All</span>
                                            </button>
                                        </li>
                                    )}
                                    {!subCategoriesLoading && visibleSubCategories.map((item, index) => {
                                        const subCategoryId = getSubCategoryId(item, index);
                                        const isActive = String(selectedSubCategoryId) === subCategoryId;
                                        const count = getSubCategoryCount(item);

                                        return (
                                            <li className="mb-12" key={subCategoryId}>
                                                <button
                                                    type="button"
                                                    className={`shop-subcategory-btn ${isActive ? "active" : ""}`}
                                                    onClick={() => onSubCategorySelect?.(item)}
                                                    aria-pressed={isActive}
                                                >
                                                    <span>{getSubCategoryTitle(item, index)}</span>
                                                    {count ? <span>{count}</span> : null}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            {/* <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Price
                                </h6>
                                <div className="custom--range">
                                    <ReactSlider
                                        className="horizontal-slider"
                                        thumbClassName="example-thumb"
                                        trackClassName="example-track"
                                        defaultValue={[0, 100]}
                                        ariaLabel={['Lower thumb', 'Upper thumb']}
                                        ariaValuetext={state => `Thumb value ${state.valueNow}`}
                                        renderThumb={(props, state) => {
                                            const { key, ...restProps } = props;
                                            return <div {...restProps} key={state.index}>{state.valueNow}</div>;
                                        }}
                                        pearling
                                        minDistance={10}
                                    />

                                    <br />
                                    <br />
                                    <div className="flex-between flex-wrap-reverse gap-8 mt-24 ">
                                        <button type="button" className="btn bg-color-primary h-40 flex-align">
                                            Filter{" "}
                                        </button>

                                    </div>
                                </div>
                            </div> */}

                            {/* <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Rating
                                </h6>
                                <div className="flex-align gap-8 position-relative mb-20">
                                    <label
                                        className="position-absolute w-100 h-100 cursor-pointer"
                                        htmlFor="rating5"
                                    >
                                        {" "}
                                    </label>
                                    <div className="common-check common-radio mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            id="rating5"
                                        />
                                    </div>
                                    <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={70}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <div
                                            className="progress-bar bg-main-600 rounded-pill"
                                            style={{ width: "70%" }}
                                        />
                                    </div>
                                    <div className="flex-align gap-4">
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    </div>
                                    <span className="text-gray-900 flex-shrink-0">124</span>
                                </div>
                                <div className="flex-align gap-8 position-relative mb-20">
                                    <label
                                        className="position-absolute w-100 h-100 cursor-pointer"
                                        htmlFor="rating4"
                                    >
                                        {" "}
                                    </label>
                                    <div className="common-check common-radio mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            id="rating4"
                                        />
                                    </div>
                                    <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={50}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <div
                                            className="progress-bar bg-main-600 rounded-pill"
                                            style={{ width: "50%" }}
                                        />
                                    </div>
                                    <div className="flex-align gap-4">
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    </div>
                                    <span className="text-gray-900 flex-shrink-0">52</span>
                                </div>
                                <div className="flex-align gap-8 position-relative mb-20">
                                    <label
                                        className="position-absolute w-100 h-100 cursor-pointer"
                                        htmlFor="rating3"
                                    >
                                        {" "}
                                    </label>
                                    <div className="common-check common-radio mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            id="rating3"
                                        />
                                    </div>
                                    <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={35}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <div
                                            className="progress-bar bg-main-600 rounded-pill"
                                            style={{ width: "35%" }}
                                        />
                                    </div>
                                    <div className="flex-align gap-4">
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    </div>
                                    <span className="text-gray-900 flex-shrink-0">12</span>
                                </div>
                                <div className="flex-align gap-8 position-relative mb-20">
                                    <label
                                        className="position-absolute w-100 h-100 cursor-pointer"
                                        htmlFor="rating2"
                                    >
                                        {" "}
                                    </label>
                                    <div className="common-check common-radio mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            id="rating2"
                                        />
                                    </div>
                                    <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={20}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <div
                                            className="progress-bar bg-main-600 rounded-pill"
                                            style={{ width: "20%" }}
                                        />
                                    </div>
                                    <div className="flex-align gap-4">
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    </div>
                                    <span className="text-gray-900 flex-shrink-0">5</span>
                                </div>
                                <div className="flex-align gap-8 position-relative mb-0">
                                    <label
                                        className="position-absolute w-100 h-100 cursor-pointer"
                                        htmlFor="rating1"
                                    >
                                        {" "}
                                    </label>
                                    <div className="common-check common-radio mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            id="rating1"
                                        />
                                    </div>
                                    <div
                                        className="progress w-100 bg-gray-100 rounded-pill h-8"
                                        role="progressbar"
                                        aria-label="Basic example"
                                        aria-valuenow={5}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <div
                                            className="progress-bar bg-main-600 rounded-pill"
                                            style={{ width: "5%" }}
                                        />
                                    </div>
                                    <div className="flex-align gap-4">
                                        <span className="text-xs fw-medium text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-medium text-gray-400 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    </div>
                                    <span className="text-gray-900 flex-shrink-0">2</span>
                                </div>
                            </div> */}
                            {/* <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Color
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-black">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color1"
                                            />
                                            <label className="form-check-label" htmlFor="color1">
                                                Black(12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-primary">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color2"
                                            />
                                            <label className="form-check-label" htmlFor="color2">
                                                Blue (12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-gray">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color3"
                                            />
                                            <label className="form-check-label" htmlFor="color3">
                                                Gray (12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-success">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color4"
                                            />
                                            <label className="form-check-label" htmlFor="color4">
                                                Green (12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-danger">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color5"
                                            />
                                            <label className="form-check-label" htmlFor="color5">
                                                Red (12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio checked-white">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color6"
                                            />
                                            <label className="form-check-label" htmlFor="color6">
                                                White (12)
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-0">
                                        <div className="form-check common-check common-radio checked-purple">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="color7"
                                            />
                                            <label className="form-check-label" htmlFor="color7">
                                                Purple (12)
                                            </label>
                                        </div>
                                    </li>
                                </ul>
                            </div> */}
                            {/* <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filter by Brand
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="brand1"
                                            />
                                            <label className="form-check-label" htmlFor="brand1">
                                                Apple
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="brand2"
                                            />
                                            <label className="form-check-label" htmlFor="brand2">
                                                Samsung
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="brand3"
                                            />
                                            <label className="form-check-label" htmlFor="brand3">
                                                Microsoft
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="brand4"
                                            />
                                            <label className="form-check-label" htmlFor="brand4">
                                                Apple
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="brand5"
                                            />
                                            <label className="form-check-label" htmlFor="brand5">
                                                HP
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-24">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="DELL"
                                            />
                                            <label className="form-check-label" htmlFor="DELL">
                                                DELL
                                            </label>
                                        </div>
                                    </li>
                                    <li className="mb-0">
                                        <div className="form-check common-check common-radio">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="color"
                                                id="Redmi"
                                            />
                                            <label className="form-check-label" htmlFor="Redmi">
                                                Redmi
                                            </label>
                                        </div>
                                    </li>
                                </ul>
                            </div> */}
                            {/* <div className="shop-sidebar__box rounded-8">
                                <img src={`${process.env.PUBLIC_URL}/assets/images/thumbs/advertise-img1.png`} alt="" />
                            </div> */}
                        </div>
                    </div>
                    {/* Sidebar End */}
                    {/* Content Start */}
                    <div className="col-lg-9 shop-content-column">
                        <div className="shop-mobile-controls d-lg-none">
                            <aside className="shop-mobile-rail" aria-label="Sub categories">
                                {!subCategoriesLoading && (
                                    <button
                                        type="button"
                                        className={`shop-mobile-category ${String(selectedSubCategoryId) === "0" ? "active" : ""}`}
                                        onClick={() => onSubCategorySelect?.({ id: 0, title: "All" })}
                                        aria-pressed={String(selectedSubCategoryId) === "0"}
                                    >
                                        <span className="shop-mobile-category__image">
                                            <img src={DUMMY_SUBCATEGORY_IMAGES[0]} alt="" />
                                        </span>
                                        <span className="shop-mobile-category__name">All</span>
                                    </button>
                                )}
                                {subCategoriesLoading && (
                                    Array.from({ length: 6 }).map((_, index) => (
                                        <span className="shop-mobile-category is-loading" key={index}>
                                            <span className="shop-mobile-category__image" />
                                            <span className="shop-mobile-category__name">Loading</span>
                                        </span>
                                    ))
                                )}
                                {!subCategoriesLoading && visibleSubCategories.map((item, index) => {
                                    const subCategoryId = getSubCategoryId(item, index);
                                    const isActive = String(selectedSubCategoryId) === subCategoryId;
                                    const title = getSubCategoryTitle(item, index);

                                    return (
                                        <button
                                            type="button"
                                            className={`shop-mobile-category ${isActive ? "active" : ""}`}
                                            key={subCategoryId}
                                            onClick={() => onSubCategorySelect?.(item)}
                                            aria-pressed={isActive}
                                        >
                                            <span className="shop-mobile-category__image">
                                                <img src={getSubCategoryImage(item, index + 1)} alt="" />
                                            </span>
                                            <span className="shop-mobile-category__name">{title}</span>
                                        </button>
                                    );
                                })}
                            </aside>

                            <div className="shop-mobile-main-head">
                                <label className="shop-mobile-search">
                                    <i className="ph ph-magnifying-glass" />
                                    <input
                                        type="search"
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                        placeholder="Search for product"
                                        aria-label="Search for product"
                                    />
                                    <span className="shop-mobile-search__barcode" aria-hidden="true">
                                        <span />
                                        <span />
                                        <span />
                                        <span />
                                        <span />
                                        <span />
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Top Start */}
                        <div className="shop-desktop-top flex-between gap-16 flex-wrap mb-40 ">
	                            <span className="text-gray-900">
                                    {loading
                                        ? "Loading products..."
                                        : `Showing ${displayedProducts.length}${paginationInfo.total ? ` of ${paginationInfo.total}` : ""} result`}
                                </span>
                            <div className="position-relative flex-align gap-16 flex-wrap">
                                <div className="list-grid-btns flex-align gap-16">
                                    <button onClick={() => setGrid(true)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid === true && "border-main-600 text-white bg-color-primary"}`}
                                    >
                                        <i className="ph-bold ph-list-dashes" />
                                    </button>
                                    <button onClick={() => setGrid(false)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${grid === false && "border-main-600 text-white bg-color-primary"}`}
                                    >
                                        <i className="ph ph-squares-four" />
                                    </button>
                                </div>
                                <div className="position-relative text-gray-500 flex-align gap-4 text-14">
                                    <label htmlFor="sorting" className="text-inherit flex-shrink-0">
                                        Sort by:{" "}
                                    </label>
                                    <select defaultValue={1}
                                        className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
                                        id="sorting"
                                    >
                                        <option value={1} >
                                            Popular
                                        </option>
                                        <option value={1}>Latest</option>
                                        <option value={1}>Trending</option>
                                        <option value={1}>Matches</option>
                                    </select>
                                </div>
                                <button onClick={sidebarController}
                                    type="button"
                                    className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn"
                                >
                                    <i className="ph-bold ph-funnel" />
                                </button>
                            </div>
                        </div>
	                        {/* Top End */}
	                        <div className={`list-grid-wrapper ${grid && "list-view"}`}>
                                {loading && renderProductLoader()}
                                {!loading && error && (
                                    <div className="shop-empty-state border border-gray-100 rounded-8">
                                        {error}
                                    </div>
                                )}
                                {!loading && !error && displayedProducts.length === 0 && (
                                    <div className="shop-empty-state border border-gray-100 rounded-8">
                                        No products found.
                                    </div>
                                )}
		                            {!loading && !error && displayedProducts.map((item, index) => {
                                    const detailLink = getProductDetailLink({ ...item, currency: getProductCurrency(item) }, { ...productDetailContext, cartContext });
                                    const cartItem = getCartItem(item, index);
                                    const quantity = Number(cartItem?.quantity || 0);
                                    const productTitle = getProductTitle(item, index);
                                    const outOfStock = isProductOutOfStock(item);
                                    const portions = getProductPortions(item);
                                    const selectedPortion = getSelectedPortion(item, index);
                                    const originalPrice = getOriginalPrice(item, selectedPortion);

                                    return (
	                                <div className={`product-card shop-product-card h-100 p-16 border border-gray-100 hover-border-main-four-600 rounded-16 position-relative transition-2 ${outOfStock ? "is-out-of-stock" : ""}`} key={getProductId(item, index)}>
                                    <span className="product-card__badge shop-product-card__badge bg-primary-600 px-8 py-4 text-sm text-white position-absolute">
                                        Best Sale
                                    </span>
                                    <Link
                                        {...detailLink}
                                        className="product-card__thumb shop-product-card__thumb flex-center rounded-8 position-relative"
                                    >
                                        <ProductImage
                                            src={getProductImage(item)}
                                            alt={productTitle}
                                            className="w-100 h-100"
                                        />
                                        <span className="shop-product-card__favorite" aria-hidden="true">
                                            <i className="ph-fill ph-heart" />
                                        </span>
                                        <span className="shop-product-card__rating-badge">
                                            {getProductRating(item)} <i className="ph-fill ph-star" />
                                        </span>
                                        {outOfStock ? (
                                            <span className="shop-product-card__stock">Out of Stock</span>
                                        ) : (
                                            <span className="shop-product-card__exclusive">Exclusive</span>
                                        )}
                                    </Link>
                                    <div className="product-card__content mt-16">
                                        <h6 className="title text-lg mt-12 mb-8 product-cart-title">
                                            <Link
                                                {...detailLink}
                                                className="link shop-product-card__title-link"
                                                tabIndex={0}
                                            >
                                                {productTitle}
                                            </Link>
                                        </h6>
                                        {/* <div className="shop-product-card__rating flex-align gap-6">
                                            <span className="text-xs fw-medium text-gray-500">{getProductRating(item)}</span>
                                            <span className="text-15 fw-medium text-warning-600 d-flex">
                                                <i className="ph-fill ph-star" />
                                            </span>
                                            {getProductRatingCount(item) && (
                                                <span className="text-xs fw-medium text-gray-500">({getProductRatingCount(item)})</span>
                                            )}
                                        </div> */}
                                        <div className="product-card__price">
                                            <span className="text-heading text-md">
                                                {getProductCurrency(item)} {getProductPrice(item, selectedPortion)}
                                            </span>
                                            {hasPrice(originalPrice) && (
                                                <del className="shop-product-card__original-price text-gray-400 text-md text-decoration-line-through">
                                                    {getProductCurrency(item)} {originalPrice}
                                                </del>
                                            )}

                                        </div>
                                        <div className="shop-product-card__unit">
                                            {portions.length > 1 ? (
                                                <button
                                                    type="button"
                                                    className="shop-product-card__unit-picker"
                                                    aria-label={`${productTitle} unit`}
                                                    aria-haspopup="dialog"
                                                    disabled={cartState.saveCartLoading}
                                                    onClick={() => setPortionPicker({ item, index })}
                                                >
                                                    <span>{selectedPortion.portion}</span>
                                                    <i className="ph ph-caret-down" aria-hidden="true" />
                                                </button>
                                            ) : (
                                                <span className="shop-product-card__unit-text">{selectedPortion?.portion || getProductUnit(item)}</span>
                                            )}
                                        </div>
                                        {outOfStock ? (
                                            <button
                                                type="button"
                                                className="product-card__cart shop-product-card__notify btn bg-color-primary text-white hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium"
                                                tabIndex={0}
                                            >
                                                <i className="ph-fill ph-bell-ringing" /> Notify
                                            </button>
                                        ) : quantity > 0 ? (
                                            <div className="shop-product-card__quantity d-flex rounded-8 overflow-hidden border border-gray-100">
                                                <button
                                                    type="button"
                                                    className="quantity__minus border-0 border-end border-gray-100 flex-shrink-0 text-neutral-600 flex-center"
                                                    onClick={() => decreaseQuantity(item, index)}
                                                    disabled={cartState.saveCartLoading}
                                                    aria-label={`Decrease ${productTitle} quantity`}
                                                >
                                                    <i className="ph ph-minus" />
                                                </button>
                                                <span className="quantity__input flex-grow-1 border-0 text-center px-4 flex-center fw-semibold">
                                                    {quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="quantity__plus border-0 border-start border-gray-100 flex-shrink-0 text-neutral-600 flex-center"
                                                    onClick={() => increaseQuantity(item, index)}
                                                    disabled={cartState.saveCartLoading}
                                                    aria-label={`Increase ${productTitle} quantity`}
                                                >
                                                    <i className="ph ph-plus" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="product-card__cart btn bg-color-primary text-white hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium"
                                                tabIndex={0}
                                                disabled={!getProductVariantId(item) || cartState.saveCartLoading}
                                                onClick={() => increaseQuantity(item, index)}
                                                aria-label={getProductVariantId(item) ? `Add ${productTitle} to basket` : `${productTitle} unavailable`}
                                            >
                                                <span className="product-card__cart-text">{getProductVariantId(item) ? "Add to Cart" : "Unavailable"}</span>
                                                <i className="ph ph-shopping-cart" />
                                            </button>
                                        )}
	                                    </div>
	                                </div>
                                    );
                                })}
                        </div>
                        {!loading && !error && paginationInfo.totalPages > 1 && (
                            <ul className="pagination flex-center flex-wrap gap-16">
                                <li className="page-item">
                                    <button
                                        type="button"
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                        disabled={paginationInfo.current <= 1}
                                        onClick={() => onPageChange?.(paginationInfo.current - 1)}
                                    >
                                        <i className="ph-bold ph-arrow-left" />
                                    </button>
                                </li>
                                {paginationPages.map((page) => (
                                    <li
                                        className={`page-item ${paginationInfo.current === page ? "active" : ""}`}
                                        key={page}
                                    >
                                        <button
                                            type="button"
                                            className="page-link h-64 w-64 flex-center text-md rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                            onClick={() => onPageChange?.(page)}
                                            disabled={paginationInfo.current === page}
                                        >
                                            {String(page).padStart(2, "0")}
                                        </button>
                                    </li>
                                ))}
                                <li className="page-item">
                                    <button
                                        type="button"
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                        disabled={paginationInfo.current >= paginationInfo.totalPages}
                                        onClick={() => onPageChange?.(paginationInfo.current + 1)}
                                    >
                                        <i className="ph-bold ph-arrow-right" />
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                    {/* Content End */}
                </div>
            </div>
        </section>
        {portionPicker && (
            <ProductPortionModal
                portions={getProductPortions(portionPicker.item)}
                currency={getProductCurrency(portionPicker.item)}
                getPortionPrice={(portion) => getProductPrice(portionPicker.item, portion)}
                submitting={cartState.saveCartLoading}
                selectedPortion={getSelectedPortion(portionPicker.item, portionPicker.index)?.portion}
                onClose={() => setPortionPicker(null)}
                onSubmit={submitPortion}
            />
        )}
        <GuestLoginRequiredModal
            show={showGuestModal}
            onClose={() => setShowGuestModal(false)}
            onContinue={handleGuestContinue}
        />
        <CartSegmentWarningModal
            show={Boolean(pendingCartItem)}
            storeName={getActiveCartStoreName(cartState, storesData)}
            submitting={cartState.saveCartLoading || cartState.getCartLoading}
            onClose={closeCartSegmentWarning}
            onConfirm={confirmCartSegmentWarning}
        />
      </>
    )
}

export default ShopSection
